<template>
    <PopupTemplate
        v-if="shouldShow"
        v-model="isOpen"
        :img-raw="imgSrc"
        img-alt="AI MCP Monitoring"
        img-class="ai-mcp-popup-anim"
        :title="t('vue.mcp_srv_now_available')"
        :body-text="t('vue.connect_to_mcp_desc')"
        @close="
            () => {
                dismissed = true;
                dismissForTtl();
            }
        "
    >
        <template #panel-decorations>
            <div class="mcp-popup-corner" aria-hidden="true">
                <div class="mcp-popup-corner-ribbon">
                    {{ t("vue.mcp_new_feature_badge") }}
                </div>
            </div>
        </template>

        <template #actions>
            <AtomicButton
                color="yellow"
                size="lg"
                class="border-transparent bg-[#088BEA] text-white hover:text-gray-100 focus:ring-[#088BEA] disabled:bg-gray-300 disabled:text-white"
                @click="visitMcpSetting"
            >
                {{ t("vue.start_with_mcp_server_now") }}
            </AtomicButton>
        </template>
    </PopupTemplate>
</template>

<script setup lang="ts">
import imgSrc from "@/assets/images/hero-ai-monitoring.svg?raw";
import PopupTemplate from "@/components/Atomic/Dialogs/PopupTemplate.vue";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import { useTranslations } from "@/composables/useTranslations.ts";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { computed, ref, watch } from "vue";

const { t } = useTranslations();
const page = usePage();

const allowedRoutesToShow = new Set([
    "home",
    "reports.index",
    "reports.show",
    "reports.show.source",
    "reports.show.ip",
    "alerts.overview",
    "alerts.settings.index",
    "blacklists.index",
]);
const currentRouteName = route().current();

const shouldShow = computed(() => {
    return (
        currentRouteName != null && allowedRoutesToShow.has(currentRouteName)
    );
});

const isOpen = ref(false);
const dismissed = ref(false);

watch(
    shouldShow,
    (show) => {
        if (!show) {
            isOpen.value = false;
            return;
        }
        dismissed.value = isDismissed();
        isOpen.value = !dismissed.value;
    },
    { immediate: true },
);

watch(isOpen, (openNow) => {
    if (!openNow && shouldShow.value) {
        dismissed.value = true;
        dismissForTtl();
    }
});

function visitMcpSetting() {
    router.visit(route("settings.mcp-server.index"));
    isOpen.value = false;
    dismissed.value = true;
    dismissForTtl();
}

const POPUP_KEY = "ui:mcp_popup_dismissed_until";
const TTL_MS = 1000 * 60 * 60;

function isDismissed(): boolean {
    const until = Number(localStorage.getItem(POPUP_KEY) ?? 0);
    return Number.isFinite(until) && until > Date.now();
}

function dismissForTtl() {
    localStorage.setItem(POPUP_KEY, String(Date.now() + TTL_MS));
}
</script>

<style scoped>
.mcp-popup-corner {
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    width: 120px;
    height: 120px;
    overflow: hidden;
    border-top-left-radius: 12px;
}

.mcp-popup-corner-ribbon {
    display: inline-flex;
    position: absolute;
    top: 30px;
    left: -55px;
    transform: rotate(-45deg);
    padding: 9px 56px;
    justify-content: center;
    align-items: center;
    gap: 3.65px;
    background: #ff421e;
    color: #ffffff;
    font-family: Inter, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
}
</style>
